import { ImageOff } from "lucide-react";
import { useParams } from "react-router-dom";
import { useImagerySlots } from "../../../hooks/use-imagery";
import { ErrorState, LoadingSpinner } from "../../ui/loading";
import { Section } from "./section";

interface ImageData {
  imagesIntro: string | null;
  photos: { id: string; url: string; caption: string }[];
}

export function SectionImages({ data }: { data: ImageData }): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const { data: slots, isLoading, isError, error } = useImagerySlots(id);

  const aerial = slots?.aerial ?? null;
  const flood = slots?.flood ?? null;

  return (
    <Section id="images" title="Images">
      {data.imagesIntro && <p className="text-sm mb-4">{data.imagesIntro}</p>}
      {isLoading && (
        <div className="flex justify-center py-6">
          <LoadingSpinner />
        </div>
      )}
      {isError && <ErrorState message={error instanceof Error ? error.message : "Failed to load images"} />}
      {!isLoading && !isError && (
        <div className="grid gap-4 sm:grid-cols-2 mb-4">
          {aerial && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Aerial Photo</p>
              <a href={aerial.url} target="_blank" rel="noopener noreferrer">
                <div className="h-48 rounded-md border bg-muted overflow-hidden">
                  <img src={aerial.url} alt="Aerial" className="h-full w-full object-cover" />
                </div>
              </a>
            </div>
          )}
          {flood && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Flood Hazard</p>
              <a href={flood.url} target="_blank" rel="noopener noreferrer">
                <div className="h-48 rounded-md border bg-muted overflow-hidden">
                  <img src={flood.url} alt="Flood hazard" className="h-full w-full object-cover" />
                </div>
              </a>
            </div>
          )}
        </div>
      )}
      {data.photos.length > 0 && (
        <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
          {data.photos.map((photo) => (
            <a key={photo.id} href={photo.url} target="_blank" rel="noopener noreferrer" className="group">
              <div className="aspect-square rounded-md border bg-muted flex items-center justify-center text-xs text-muted-foreground group-hover:border-primary/50 transition-colors">
                Photo
              </div>
              {photo.caption && <p className="text-xs text-muted-foreground mt-1 truncate">{photo.caption}</p>}
            </a>
          ))}
        </div>
      )}
      {!isLoading && !aerial && !flood && data.photos.length === 0 && (
        <div className="flex flex-col items-center gap-2 py-4 text-sm text-muted-foreground">
          <ImageOff className="h-6 w-6" />
          No images uploaded.
        </div>
      )}
    </Section>
  );
}
